import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import QRCodeStyling from 'qr-code-styling';
import { STATION_TYPES, getStationType, setStationType, roleTimeoutMin } from '../lib/sessionPolicy';
import { auth } from '../firebase/firebase-config.js';
import ChangePasswordForm from '../components/ChangePasswordForm.js';
import { needsPasswordRotation } from '../lib/passwordPolicy';
import { t } from '../lib/i18n';

// --- Iconos ---
const IconSave = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>;
const IconDownload = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>;

// --- Logo y Foto por Defecto ---
// Usar los assets LOCALES de /public (mismo origen). El URL anterior apuntaba a OTRO
// proyecto de Firebase (sistema-produccion-cartonbar.appspot.com) → imagen rota y, al
// fallar la carga con crossOrigin, el QR tampoco se generaba.
const companyLogoUrl = `${process.env.PUBLIC_URL}/cartonbar-logo.png`;   // wordmark (encabezado)
const qrLogoUrl = `${process.env.PUBLIC_URL}/cartonbar-icon.png`;        // ícono cuadrado (centro del QR)
const defaultPhoto = `${process.env.PUBLIC_URL}/cartonbar-icon.png`;


const MyProfileView = ({ user, db, storage }) => {
    const [profile, setProfile] = useState({
        name: '', jobTitle: '', publicPhone: '', publicEmail: '', photoURL: '',
        socialLinks: { website: '', linkedin: '', whatsapp: '' }
    });
    const [isLoading, setIsLoading] = useState(true);
    const qrCodeInstance = useRef(null); // Usamos useRef para mantener la instancia del QR
    const qrRef = useRef(null);

    // Tipo de estación: se guarda en ESTE navegador/computadora (no en el perfil).
    const [stationType, setStationTypeState] = useState(getStationType());
    const handleStationChange = (value) => {
        setStationType(value);
        setStationTypeState(value);
    };
    const effectiveTimeoutMin = stationType ? STATION_TYPES[stationType].timeoutMin : roleTimeoutMin(user);

    // Cargar perfil del usuario
    useEffect(() => {
        const fetchProfile = async () => {
            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const data = userSnap.data();
                setProfile({
                    name: data.name || user.displayName || '',
                    jobTitle: data.jobTitle || '',
                    publicPhone: data.publicPhone || '',
                    publicEmail: data.publicEmail || user.email || '',
                    photoURL: data.photoURL || user.photoURL || '',
                    socialLinks: data.socialLinks || { website: '', linkedin: '', whatsapp: '' }
                });
            }
            setIsLoading(false);
        };
        fetchProfile();
    }, [db, user]);

    // Inicializar y actualizar el QR en un solo efecto robusto
    useEffect(() => {
        if (isLoading || !qrRef.current) return;

        // Si no existe la instancia, la creamos
        if (!qrCodeInstance.current) {
            qrCodeInstance.current = new QRCodeStyling({
                width: 280,
                height: 280,
                image: qrLogoUrl,
                dotsOptions: { color: '#000000', type: 'rounded' },
                imageOptions: { hideBackgroundDots: true, imageSize: 0.3, margin: 4, crossOrigin: 'anonymous' },
            });
            qrCodeInstance.current.append(qrRef.current);
        }
        
        // Formato vCard
        const nameParts = profile.name.split(' ');
        const lastName = nameParts.pop() || '';
        const firstName = nameParts.join(' ');
        
        const vCard = `BEGIN:VCARD
VERSION:3.0
N:${lastName};${firstName}
FN:${profile.name}
TITLE:${profile.jobTitle}
ORG:CARTONBAR
TEL;TYPE=WORK,VOICE:${profile.publicPhone}
EMAIL:${profile.publicEmail}
URL;TYPE=Website:${profile.socialLinks.website || ''}
URL;TYPE=LinkedIn:${profile.socialLinks.linkedin || ''}
URL;TYPE=WhatsApp:https://wa.me/${profile.socialLinks.whatsapp || ''}
PHOTO;VALUE=URL:${profile.photoURL || ''}
END:VCARD`;
        
        // Actualizamos los datos del QR existente
        qrCodeInstance.current.update({ data: vCard });

    }, [profile, isLoading]); // Se ejecuta cuando el perfil cambia y la carga ha terminado

    const handleChange = (e) => setProfile(p => ({ ...p, [e.target.name]: e.target.value }));
    const handleLinkChange = (e) => setProfile(p => ({ ...p, socialLinks: { ...p.socialLinks, [e.target.name]: e.target.value } }));
    
    const [photoUploading, setPhotoUploading] = useState(false);
    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        // Fallback: si la vista no recibió `storage` por props, lo obtenemos del app.
        const storageInstance = storage || getStorage();
        const storageRef = ref(storageInstance, `user_photos/${user.uid}/${Date.now()}_${file.name}`);
        setPhotoUploading(true);
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on('state_changed', null,
            (error) => { console.error("Upload error:", error); setPhotoUploading(false); alert("No se pudo subir la foto: " + (error?.message || error)); },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                setProfile(p => ({ ...p, photoURL: url }));
                // Persistir de inmediato para que la foto no se pierda si no se pulsa "Guardar".
                try { await updateDoc(doc(db, 'users', user.uid), { photoURL: url }); } catch (_) {}
                setPhotoUploading(false);
            }
        );
    };

    const handleSaveProfile = async () => {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { ...profile });
        alert("Perfil guardado con éxito!");
    };

    const handleDownloadQR = () => {
        if (qrCodeInstance.current) {
            qrCodeInstance.current.download({ name: `vcard-qr-${profile.name.replace(/\s/g, '_')}`, extension: 'png' });
        } else {
            alert("El código QR aún no se ha generado. Por favor, espere un momento.");
        }
    };
    
    if (isLoading) return <div className="p-6">Cargando perfil...</div>;

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Mi Perfil y vCard QR</h1>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Información de Contacto Público</h2>
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <img src={profile.photoURL || defaultPhoto} alt="Perfil" className="w-24 h-24 rounded-full object-cover bg-gray-100" />
                            <div>
                                <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={photoUploading} className="text-sm" />
                                {photoUploading && <p className="text-xs text-blue-600 mt-1">Subiendo foto…</p>}
                            </div>
                        </div>
                        <input type="text" name="name" value={profile.name} onChange={handleChange} placeholder="Nombre Completo" className="w-full p-2 border rounded" />
                        <input type="text" name="jobTitle" value={profile.jobTitle} onChange={handleChange} placeholder="Cargo (Ej: Ejecutivo de Ventas)" className="w-full p-2 border rounded" />
                        <input type="tel" name="publicPhone" value={profile.publicPhone} onChange={handleChange} placeholder="Teléfono Público" className="w-full p-2 border rounded" />
                        <input type="email" name="publicEmail" value={profile.publicEmail} onChange={handleChange} placeholder="Email Público" className="w-full p-2 border rounded" />
                        <h3 className="text-lg font-semibold pt-4 border-t">Enlaces y Redes Sociales</h3>
                        <input type="url" name="website" value={profile.socialLinks.website} onChange={handleLinkChange} placeholder="https://tu-pagina-web.com" className="w-full p-2 border rounded" />
                        <input type="url" name="linkedin" value={profile.socialLinks.linkedin} onChange={handleLinkChange} placeholder="https://linkedin.com/in/tu-perfil" className="w-full p-2 border rounded" />
                        <input type="tel" name="whatsapp" value={profile.socialLinks.whatsapp} onChange={handleLinkChange} placeholder="Número de WhatsApp (Ej: 15551234567)" className="w-full p-2 border rounded" />
                    </div>
                     <button onClick={handleSaveProfile} className="mt-6 w-full flex justify-center items-center gap-2 px-4 py-2 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700"><IconSave />Guardar Perfil</button>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-center mb-4">Previsualización de vCard</h2>
                        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 flex flex-col items-center">
                            <img src={companyLogoUrl} alt="Logo Cartonbar" className="h-8 mb-4"/>
                            <img src={profile.photoURL || defaultPhoto} alt="Perfil" className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"/>
                            <h3 className="text-lg font-bold mt-3">{profile.name || 'Nombre Apellido'}</h3>
                            <p className="text-sm text-gray-500">{profile.jobTitle || 'Cargo en la Empresa'}</p>
                            <div ref={qrRef} className="mt-4" />
                        </div>
                    </div>
                    <div className="w-full mt-4">
                        <button onClick={handleDownloadQR} className="w-full flex justify-center items-center gap-2 px-4 py-2 font-bold text-white bg-gray-700 rounded-lg hover:bg-gray-800"><IconDownload />Descargar QR</button>
                        <p className="text-xs text-gray-500 mt-2 text-center">Un cliente puede escanear el código para añadirte a sus contactos.</p>
                    </div>
                </div>
            </div>

            {/* Esta computadora (estación de trabajo) — controla el auto-cierre por inactividad */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-1">Esta computadora (estación de trabajo)</h2>
                <p className="text-sm text-gray-500 mb-4">
                    Se guarda <strong>solo en este navegador/PC</strong>. Define cada cuánto se cierra la sesión por
                    inactividad. Útil en máquinas compartidas (p. ej. una PC de producción mantiene el intervalo
                    corto sin importar quién entre). Si lo dejas en automático, el tiempo depende del rol del usuario.
                </p>
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Tipo de estación</label>
                        <select
                            value={stationType}
                            onChange={(e) => handleStationChange(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md text-sm min-w-[220px]"
                        >
                            <option value="">Automático (según el rol del usuario)</option>
                            {Object.values(STATION_TYPES).map((s) => (
                                <option key={s.id} value={s.id}>{s.label} — {s.timeoutMin} min</option>
                            ))}
                        </select>
                    </div>
                    <div className="text-sm text-gray-600">
                        Cierre por inactividad actual: <strong>{effectiveTimeoutMin} min</strong>
                        {!stationType && <span className="text-gray-400"> (por rol)</span>}
                    </div>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                    El cambio se aplica la próxima vez que inicies sesión (o al recargar la página). Diseño 20 min ·
                    Administración 30 min · Producción 8 min.
                </p>
            </div>

            {/* S4c — cambio de contraseña (rotación guiada; el banner de App.js apunta aquí con #password) */}
            <div id="password" className={`mt-8 bg-white p-6 rounded-lg shadow-md ${needsPasswordRotation(user) ? 'ring-2 ring-amber-400' : ''}`}>
                <h2 className="text-xl font-semibold mb-1">{t('password.section.title')}</h2>
                <ChangePasswordForm auth={auth} db={db} user={user} />
            </div>
        </div>
    );
};

export default MyProfileView;

